import { clientConfig } from '@/config/clientConfig';
import { formatDateTimeInZone } from '@/shared/lib/date-utils';
import { NSG_STATUS_LABEL, PERMIT_GRANT_STATUS_LABEL } from '@skewb-works/contracts';
import type { JobPermitNsg, JobPermitRequest } from '@skewb-works/contracts';
import { MapPin } from 'lucide-react';

interface NSGDetailsCardProps {
  nsg: JobPermitNsg | null | undefined;
  permitRequest: JobPermitRequest | null | undefined;
  /** Once a permit exists CORE updates the permit, not the request, so the NPR state is stale. */
  hasPermit: boolean;
}

function NsgDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-sm text-foreground break-words">{value}</div>
    </div>
  );
}

/**
 * Read-only NSG (National Street Gazetteer) response and notice permit request (NPR)
 * state relayed by CORE (CWL-411 storage, CWL-419 display). Both exist before any
 * permit, so this sits beside PermitCard rather than inside it. View-only (AC20):
 * NSG and NPR are actioned in Works Mobile.
 *
 * The NPR rows show only before a permit exists: afterwards CORE updates the permit,
 * which PermitCard already shows, so the request row would be stale.
 *
 * Renders only populated rows; returns null when there is nothing to show.
 */
export function NSGDetailsCard({ nsg, permitRequest, hasPermit }: NSGDetailsCardProps) {
  const request = hasPermit ? null : permitRequest;
  const rows = [
    { key: 'nsg-status', label: 'NSG Status', value: nsg ? NSG_STATUS_LABEL[nsg.status] : null },
    { key: 'usrn', label: 'USRN', value: nsg?.usrn ?? null },
    {
      key: 'special-designation',
      label: 'Special Designation Code',
      value: nsg?.special_designation_code ?? null,
    },
    {
      key: 'local-considerations',
      label: 'Local Considerations',
      value: nsg?.local_considerations ?? null,
    },
    {
      key: 'received-at',
      label: 'Received At',
      value: formatDateTimeInZone(nsg?.received_at, clientConfig.features.schedulingTimeZone),
    },
    {
      key: 'npr-status',
      label: 'NPR Status',
      value: request?.state ? PERMIT_GRANT_STATUS_LABEL[request.state] : null,
    },
    {
      key: 'npr-reason',
      label: request?.state ? 'NPR Reason' : 'Reason',
      value: request?.reason ?? null,
    },
  ].filter((row): row is typeof row & { value: string } => Boolean(row.value));

  if (rows.length === 0) return null;

  return (
    <div data-testid="nsg-card" className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-sky-50 dark:bg-sky-950 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-sky-600 dark:text-sky-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">NSG &amp; Notice Request</h3>
          <p className="text-xs text-muted-foreground">Received from CORE</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {rows.map((row) => (
          <NsgDetailRow key={row.key} label={row.label} value={row.value} />
        ))}
      </div>
    </div>
  );
}
